import React from "react";
import RightworkSelect from "../../../../../components/shared/RightworkSelect";

const isSalaryType = (wageType) =>
  ["salary", "salaried"].includes(String(wageType || "").trim().toLowerCase());

// Hourly or salary. Rightwork wages: the backend's current all-roles wage
// (resolved on the store's date, as for stm.wage_type) decides; otherwise
// the POS-synced wage_type does.
export function payTypeOf(wageData) {
  const currentWage = wageData?.current_wage;
  if (wageData?.pay_source === "rightwork" && currentWage) {
    return currentWage.compensation_type === "salary" ? "salary" : "hourly";
  }
  return isSalaryType(wageData?.wage_type) ? "salary" : "hourly";
}

// Mirrors user_info.labor_exempt: an explicit labor_exempt wins, otherwise
// salaried (by wage_type) is exempt. An hourly member can only be exempt by
// an explicit override, which gets its own state so it can be cleared.
export function classificationOf(payType, laborExempt, wageType) {
  if (payType === "hourly") return laborExempt === true ? "hourly_exempt" : "hourly";
  const exempt = laborExempt ?? isSalaryType(wageType);
  return exempt ? "salary_exempt" : "salary_non_exempt";
}

export const LABOR_EXEMPT_FOR = { hourly: null, hourly_exempt: true, salary_non_exempt: false, salary_exempt: true };

const LABELS = {
  hourly: "Hourly",
  hourly_exempt: "Hourly (exempt)",
  salary_non_exempt: "Salary (non-exempt)",
  salary_exempt: "Salary (exempt)",
};

const EXPLANATIONS = {
  hourly: "Overtime, breaks and other labor law rules apply.",
  hourly_exempt: "Paid hourly but marked exempt, so there are no labor law flags. Choose Hourly to apply labor law rules.",
  salary_non_exempt: "Paid a salary, but overtime, breaks and other labor law rules still apply.",
  salary_exempt: "No labor law flags. Clock-in is optional by default.",
};

export const SOURCE_LABELS = { toast: "Toast", none: "your POS" };

export const payTypeOfClassification = (classification) =>
  String(classification || "").startsWith("hourly") ? "hourly" : "salary";

// The wage's pending_exempt_status for a classification: applied when that
// wage takes effect. Hourly follows pay type unless it keeps an exemption.
export const PENDING_EXEMPT_STATUS_FOR = {
  hourly: "pay_type",
  hourly_exempt: "exempt",
  salary_non_exempt: "non_exempt",
  salary_exempt: "exempt",
};

// The choices offered when the saved classification is `current`.
// "Hourly (exempt)" is offered only while it's the current state, so an old
// override shows and can be cleared but isn't a new choice. With POS pay,
// hourly vs salary comes from the POS, so only exempt status can change.
export function classificationOptions(current, fromPos) {
  const currentPayType = payTypeOfClassification(current);
  return ["hourly", ...(current === "hourly_exempt" ? ["hourly_exempt"] : []), "salary_non_exempt", "salary_exempt"]
    .map((choice) => ({
      value: choice,
      label: LABELS[choice],
      disabled: fromPos && payTypeOfClassification(choice) !== currentPayType,
    }));
}

/**
 * The pay classification dropdown, as a controlled form field. `current` is
 * the saved classification (it decides the choices); `value` is the edit.
 */
export default function PayClassificationField({ id, value, current, onChange, source, disabled }) {
  const fromPos = source !== "rightwork";
  const options = classificationOptions(current, fromPos);
  // A POS-hourly member has nothing to change unless an override needs clearing.
  const locked = fromPos && current === "hourly";
  return (
    <div>
      <label id={`${id}-label`} htmlFor={id} className="mb-2 block text-sm font-medium text-slate-900">
        Pay classification
      </label>
      <div className="max-w-sm">
        <RightworkSelect
          id={id}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          options={options}
          disabled={disabled || locked}
          describedBy={`${id}-help`}
        />
      </div>
      <p id={`${id}-help`} className="mt-2 text-sm leading-5 text-slate-500">
        {EXPLANATIONS[value]}
        {fromPos && ` Hourly or salary comes from ${SOURCE_LABELS[source] || "your POS"}.`}
      </p>
    </div>
  );
}
